import http from '@gp/utils/httpTS';
import { STUDIO_MAP } from '@gp/pages/StudioDashboard/StudioOverall/constants';
import { RES_FIELDS_MAP } from '@gp/pages/StudioDashboard/StudioOverall/Tabs/Projects/components/Live/components/BreakdownList/constants';
import { ProjectSuccessDataType } from '@gp/types/project';

// 获取Studio Overall页面 Overview Tab页 页面级数据
export const fetchStudioOverallOverviewPageData = (params?: {
  page?:
  | 'information_glance'
  | 'key_issue'
  | 'board_governance'
  | 'finance'
  | 'people'
  | 'projects'
  | 'calendar'
  | 'finance_5y';
}): Promise<any> => {
  return http.get('/api/v1/globalpedia_pc/studio_overview/page_public', {
    params: params || {},
  });
};

// 获取Studio Overall页面 Overview Tab页 KeyMetrics模块数据
export const fetchStudioOverallOverviewKeyMetricsData = () => {
  return http.get('/api/v1/globalpedia_pc/studio_overview/key_metrics');
};

type IType = {
  name: string;
  revenue: string;
  target: string;
  completion: string;
};
// 获取Studio Overall页面 Overview Tab页 Financial Overview堆叠直方图数据
export const fetchStudioOverallOverviewVisualisData = () => {
  return http.get('/api/v1/globalpedia_pc/studio_overview/financial_overviewV2', {
    formatData: (res: { revenue: IType[]; profit: IType[]; update_time: string }) => {
      // 对数据进行固定的STUDIO_MAP排序
      const revenue: IType[] = [];
      const profit: IType[] = [];
      for (let i = 0; i < STUDIO_MAP?.length; i++) {
        const t = STUDIO_MAP?.[i];
        const r = res?.revenue;
        const f = res?.profit;
        if (r && Array.isArray(r)) {
          const a = r?.filter((item) => item?.name && item?.name?.toLowerCase() == t)?.[0];
          a && revenue?.push(a);
        }
        if (f && Array.isArray(f)) {
          const a = f?.filter((item) => item?.name && item?.name?.toLowerCase() == t)?.[0];
          a && profit?.push(a);
        }
      }

      return {
        revenue,
        profit,
        dataTime: res?.update_time,
      };
    },
  });
};

// 获取Studio Overall页面 Overview Tab页 Key Information At A Glance宽表数据
export const fetchStudioOverallOverviewTableData = (params: IStudioOverallOverviewKeyInformationTableFetcherParams) => {
  return http.post('/api/v1/globalpedia_pc/studio_overview/information_glanceV2', params);
};

// 下载Studio Overall页面 Overview Tab页 Key Information At A Glance宽表数据
export const downloadStudioOverallOverviewTableData = (
  params: IStudioOverallOverviewKeyInformationTableFetcherParams & {
    file_type: number;
  },
): Promise<any> => {
  // @ts-ignore
  return http({
    url: '/api/v1/globalpedia_pc/studio_overview/information_glance_download',
    method: 'post',
    data: params,
    original: true,
    responseType: 'blob',
    download: true,
    errorHandler: true,
  });
};

// 获取Studio Overall页面 Management 页签Key Issues列表数据
export const fetchStudioOverallManagementKeyIssuesData = (params: {
  studio: string[];
  business_owner: string[];
}): Promise<{
  issue_list: IStudioOverallManagementKeyIssuesResultData;
  update_time: string;
}> => {
  return http.post('/api/v1/globalpedia_pc/studio_overview/key_issues', params);
};

// 获取Studio Overall页面 Management 页签Board Governance列表数据
export const fetchStudioOverallManagementBoardGovernanceData = (params: {
  studio: string[];
  business_owner: string[];
  date: string;
}): Promise<IStudioOverallManagementBoardGovernanceResultData> => {
  return http.post('/api/v1/globalpedia_pc/studio_overall/board_governance', params);
};

// 获取Studio Overall页面 Finance 页签Overview列表数据
export const fetchStudioOverallFinanceOverviewData = (params: {
  management_team: string[];
}): Promise<IStudioOverallFinanceOverviewResultData> => {
  return http.post('/api/v1/globalpedia_pc/studio_overall/finance/overview', params);
};

// 获取Studio Overall页面 Finance 页签Overview子弹图下载
export const studioOverallFinanceOverviewDownLoad = (params: {
  management_team: string[];
  metrics: 'revenue' | 'profit';
  file_type: 1 | 2; // 1: csv 2: excal
}) => {
  // @ts-ignore
  return http({
    url: '/api/v1/globalpedia_pc/studio_overall/finance/overview/download',
    method: 'post',
    data: params,
    original: true,
    responseType: 'blob',
    download: true,
    errorHandler: true,
  });
};

// 获取Studio Overall页面 Finance 页签5-Year Forecast列表数据
export const fetchStudioOverallFinanceForecastListData = (params: {
  management_team: string[];
  data_types: {
    [key: string]: string[];
  };
}): Promise<IStudioOverallFinanceForecastListResultData> => {
  return http.post('/api/v1/globalpedia_pc/studio_overall/finance/year_forecast/breakdown', params);
};

// 获取Studio Overall页面 Finance 页签5-Year Forecast卡片数据
export const fetchStudioOverallFinanceForecastCardData = (params: {
  management_team: string[];
  data_types: {
    [key: string]: string[];
  };
  order_data: 'base_scenario' | 'conservative_scenario' | ''; // 哪一个卡片
  order: 'asc' | 'desc' | '';
  order_metric: 'Date' | 'Revenue' | 'Profit' | 'Margin' | '' | (string & {});
}): Promise<IStudioOverallFinanceForecastCardResultData> => {
  return http.post('/api/v1/globalpedia_pc/studio_overall/finance/year_forecast', params);
};

// 获取Studio Overall页面 Finance 页签5-Year Forecast卡片下载
export const studioOverallFinanceForecastCardDownLoad = (params: {
  management_team: string[];
  data_types: {
    [key: string]: string[];
  };
  // metrics: 'base' | 'conservative',
  file_type: 1 | 2; // 1: csv 2: excal
  order_data: 'base_scenario' | 'conservative_scenario' | ''; // 哪一个卡片
  order: 'asc' | 'desc' | '';
  order_metric: 'Date' | 'Revenue' | 'Profit' | 'Margin' | '' | (string & {});
}) => {
  // @ts-ignore
  return http({
    url: '/api/v1/globalpedia_pc/studio_overall/finance/year_forecast/download',
    method: 'post',
    data: params,
    original: true,
    responseType: 'blob',
    download: true,
    errorHandler: true,
  });
};

// 获取Studio Overall页面People中时间筛选器的时间范围
export const fetchStudioOverallPeopleTimeRange = (): Promise<{
  start_date: string;
  end_date: string;
}> => {
  return http.get('/api/v1/globalpedia_pc/studio_overall/people/time_range');
};

// 获取Studio Overall页面People中图表信息
/* export const fetchStudioOverallPeopleChartData = (params: {
  'start_date': string,
  'end_date': string,
  'management_team': string[],
  'metrics': string[], // headcount, headcount_cost, 5y_headcounts
}): Promise<IStudioOverallPeopleChartResultData> => {
  return http.post('/api/v1/globalpedia_pc/studio_overall/people/pile_chart', params);
}; */

export const fetchStudioOverallPeopleHeadcountsData = (params: {
  start_date: string;
  end_date: string;
  management_team: string[];
  order_metric?: string;
  order?: string;
}): Promise<IStudioOverallPeopleChartResultData> => {
  return http.post('/api/v1/globalpedia_pc/studio_overall/people/pile_chart_hc', params);
};
export const fetchStudioOverallPeopleHeadcountCostData = (params: {
  start_date: string;
  end_date: string;
  management_team: string[];
  order_metric?: string;
  order?: string;
}): Promise<IStudioOverallPeopleChartResultData> => {
  return http.post('/api/v1/globalpedia_pc/studio_overall/people/pile_chart_cost', params);
};
export const fetchStudioOverallPeopleFiveYearHeadcountsData = (params: {
  start_date: string;
  end_date: string;
  management_team: string[];
  order_metric?: string;
  order?: string;
}): Promise<IStudioOverallPeopleChartResultData> => {
  return http.post('/api/v1/globalpedia_pc/studio_overall/people/pile_chart_5y', params);
};

// 获取Studio Overall页面People中图表下载
export const studioOverallPeopleChartDownLoad = (params: {
  file_type: 1 | 2; // 1: csv 2: excal
  start_date: string;
  end_date: string;
  management_team: string[];
  metrics: string[]; // headcount, headcount_cost, 5y_headcounts
  order_metric?: string;
  order?: string;
}) => {
  // @ts-ignore
  return http({
    url: '/api/v1/globalpedia_pc/studio_overall/people/pile_chart/download',
    method: 'post',
    data: params,
    original: true,
    responseType: 'blob',
    download: true,
    errorHandler: true,
  });
};

// 获取Studio Overall页面People中列表信息
export const fetchStudioOverallPeopleListData = (params: {
  management_team: string[];
  order: string; // asc/desc
  order_metric: string;
}): Promise<IStudioOverallPeopleListResultData> => {
  return http.post('/api/v1/globalpedia_pc/studio_overall/people/breakdown', params);
};

// 获取Studio Overall页面Projects页面Development中Key Metrics信息
export const fetchStudioOverallProjectsKeyMetricsData = (params: {
  bo: string[]; // 名称
  project: string[]; // id
}): Promise<IStudioOverallProjectsKeyMetricsData> => {
  return http.post('/api/v1/globalpedia_pc/studio_overall/projects/dev/key_metrics', params);
};

// 获取Studio Overall页面Projects页面Development中Overview信息
export const fetchStudioOverallProjectsOverviewData = (params: {
  bo: string[]; // 名称
  project: string[]; // id
  project_list_req: {
    [keyName: string]: any; // project list页面接口相关参数
  };
}): Promise<ProjectSuccessDataType> => {
  // return http.post('/api/v1/globalpedia_pc/studio_overall/projects/dev/overview', params);
  // @ts-ignore
  return http({
    url: '/api/v1/globalpedia_pc/studio_overall/projects/dev/overview',
    method: 'POST',
    data: params,
    formatData: (res) => {
      if (!Array.isArray(res?.items)) return res;
      // 之前的completed处理成现在对应的status
      const orgData = {
        ...res,
        items: res?.items?.map((t: any) => {
          const workplans =
            t?.updates_info && t?.updates_info?.content && t?.updates_info?.content?.workplans
              ? t?.updates_info?.content?.workplans
              : [];
          const newUpdatesInfo =
            Array.isArray(workplans) && workplans?.length > 0
              ? {
                ...t?.updates_info,
                content: t?.updates_info?.content
                  ? {
                    ...t?.updates_info?.content,
                    workplans: workplans?.map((item) => {
                      return {
                        ...item,
                        status:
                              !item?.status && item?.hasOwnProperty('completed')
                                ? item?.completed
                                  ? 4
                                  : 1
                                : item?.status,
                      };
                    }),
                  }
                  : t?.updates_info?.content,
              }
              : t?.updates_info;

          return {
            ...t,
            updates_info: newUpdatesInfo,
          };
        }),
      };

      return orgData;
    },
  });
};

// 获取Studio Overall页面Projects页面Development中Budget卡片信息
export const fetchStudioOverallProjectsBudgetCardData = (params: {
  bo: string[]; // 名称
  project: string[]; // id
}): Promise<{
  year_budget_date: string;
  budget_date: string;
  year_budget: string;
  year_budget_spent_to_date: string;
  last_month_spent: string;
  total_budget: string;
  total_budget_spent_to_date: string;
} | null> => {
  return http.post('/api/v1/globalpedia_pc/studio_overall/projects/dev/budget', params);
};

// 获取Studio Overall页面Projects页面Development中Budget Spend Trends信息
export const fetchStudioOverallProjectsBudgetSpendTrendsData = (params: {
  bo: string[]; // 名称
  project: string[]; // id
  start_date: string;
  end_date: string;
}): Promise<IStudioOverallProjectsBudgetSpendTrendsData> => {
  return http.post('/api/v1/globalpedia_pc/studio_overall/projects/dev/budget_spend_trends', params);
};

// 获取Studio Overall页面Projects页面Development中Budget Spend Trends下载
export const studioOverallProjectsBudgetSpendTrendsDownLoad = (params: {
  file_type: 1 | 2; // 1: csv 2: excal
  bo: string[]; // 名称
  project: string[]; // id
  start_date: string;
  end_date: string;
}) => {
  // @ts-ignore
  return http({
    url: '/api/v1/globalpedia_pc/studio_overall/projects/dev/budget_spend_trends_download',
    method: 'post',
    data: params,
    original: true,
    responseType: 'blob',
    download: true,
    errorHandler: true,
  });
};

// 获取Studio Overall页面Projects页面Development中Cost信息
export const fetchStudioOverallProjectsCostData = (params: {
  type: 1 | 2; // 1: Monthly 2: Quarterly
  bo: string[]; // 名称
  project: string[]; // id
  order?: 'asc' | 'desc';
  order_metrics?: string;
}): Promise<IStudioOverallProjectsCostData> => {
  return http.post('/api/v1/globalpedia_pc/studio_overall/projects/dev/cost', params);
};

// 获取Studio Overal页面Projects页面Live筛选器数据
export const fetchStudioOverallProjectsLiveFilterData = (params?: {
  internal?: boolean;
}): Promise<{
  list:
  | {
    id: string;
    name: string;
    platform_id: string;
    genre: string;
  }[]
  | null;
  genre: {
    [keyName: string]:
    | {
      id: string;
      name: string;
      platform_id: string;
      genre: string;
    }[]
    | null;
  } | null;
}> => {
  return http.get('/api/v1/globalpedia_pc/studio_overall/projects/live/cond_list', {
    params,
  });
};

// 获取Studio Overall页面Projects页面Live中Key Metrics信息
export const fetchStudioOverallProjectsLiveKeyMetricsData = (params: {
  internal?: 'true' | ''; // 是internal传'true'，不是的话传''或者不传
  bo: string[];
  genre: any[];
  project_ids: any[];
  order?: 'asc' | 'desc';
  order_metric?: string;
}): Promise<IStudioOverallProjectsLiveKeyMetricsData> => {
  return http.post('/api/v1/globalpedia_pc/studio_overall/projects/live/key_metrics', params);
};

// 获取Studio Overall页面Projects页面Live中Intelligence Breakdown信息
export const fetchStudioOverallProjectsLiveIntelligenceBreakdownData = (params: {
  internal?: 'true' | ''; // 是internal传'true'，不是的话传''或者不传
  bo: string[];
  genre: any[];
  project_ids: any[];
  order?: 'asc' | 'desc';
  order_metric?: string;
}): Promise<any> => {
  return http.post('/api/v1/globalpedia_pc/studio_overall/projects/live/breakdown', params);
};

// 获取Studio Overall页面Projects页面Live中Internal Breakdown信息
export const fetchStudioOverallProjectsLiveInternalBreakdownData = (params: {
  bo?: string[];
  genre?: string[];
  order?: 'asc' | 'desc';
  order_metric?: string;
  select_id: string[];
  yoy: boolean;
  mom: boolean;
  studio_id?: string; // 单Studio页面使用
}): Promise<any> => {
  // @ts-ignore
  return http({
    url: '/api/v1/globalpedia_pc/studio_overall/projects/live/internal_breakdown',
    method: 'POST',
    data: params,
    formatData: (res) => {
      const list = [];
      const resList = res?.list;
      if (Array.isArray(resList) && resList?.length) {
        for (let i = 0, l = resList?.length; i < l; i++) {
          const t = resList?.[i];
          const obj: any = {
            total_revenue: '',
            total_revenue_ratio: '',
            total_revenue_target: '',
            total_revenue_target_ratio: '',
            ytm_total_revenue: '',
            ytm_total_revenue_ratio: '',
            ytm_total_revenue_target: '',
            ytm_total_revenue_target_ratio: '',
            total_mau: '',
            total_mau_ratio: '',
          };
          const details = t?.details;
          if (Array.isArray(details)) {
            for (let j = 0, l = details?.length; j < l; j++) {
              const k = details?.[j];
              const itemMap = RES_FIELDS_MAP?.[k?.metric];
              const key = itemMap?.key;
              if (key) {
                obj[key] = k?.value;
                if (itemMap?.yoy) {
                  obj[`${key}_ratio`] = k?.yoy;
                }
                if (itemMap?.mom) {
                  obj[`${key}_ratio`] = k?.mom;
                }
              }
            }
          }
          const {
            total_revenue = '',
            total_revenue_ratio = '',
            total_revenue_target = '',
            total_revenue_target_ratio = '',
            ytm_total_revenue = '',
            ytm_total_revenue_ratio = '',
            ytm_total_revenue_target = '',
            ytm_total_revenue_target_ratio = '',
            total_mau = '',
            total_mau_ratio = '',
          } = obj;
          list.push({
            studio_id: t?.studio_id,
            studio: t?.studio_name,
            bo: t?.bo,
            game_cover: t?.cover,
            game_id: t?.game_id,
            game_name: t?.game_name,
            genre: t?.genre,
            platform: Array.isArray(t?.platform) ? t?.platform?.join(',') : t?.platform,
            sub_genre: t?.sub_genre,
            total_mau,
            total_mau_info: [
              {
                entity_type: 'pc',
                game_id: '',
                prev_value: '',
                ratio: '',
                value: '',
              },
            ],
            total_mau_ratio,
            total_revenue,
            total_revenue_info: [
              {
                entity_type: 'pc',
                game_id: '',
                prev_value: '',
                ratio: '',
                value: '',
              },
            ],
            total_revenue_ratio,
            total_revenue_target,
            total_revenue_target_info: [
              {
                entity_type: 'pc',
                game_id: '',
                prev_value: '',
                ratio: '',
                value: '',
              },
            ],
            total_revenue_target_ratio,
            ytm_total_revenue,
            ytm_total_revenue_info: [
              {
                entity_type: 'pc',
                game_id: '',
                prev_value: '',
                ratio: '',
                value: '',
              },
            ],
            ytm_total_revenue_ratio,
            ytm_total_revenue_target,
            ytm_total_revenue_target_info: [
              {
                entity_type: 'pc',
                game_id: '',
                prev_value: '',
                ratio: '',
                value: '',
              },
            ],
            ytm_total_revenue_target_ratio,
          });
        }
      }
      return {
        ...res,
        header_date: res?.data_time ? res?.data_time?.replace('-', '.') : '',
        list,
      };
    },
  });
};

// 获取Studio Overall页面Calendar页面时间筛选器的可选时间范围
export const fetchStudioOverallCalendarTimeData = (): Promise<{
  ms_start_date: string;
  ms_end_date: string;
  mgt_start_date: string;
  mgt_end_date: string;
  update_time: string;
}> => {
  return http.post('/api/v1/globalpedia_pc/studio_overall/calendar/time_range', {});
};

// 获取Studio Overall页面Calendar页面信息
export const fetchStudioOverallCalendarData = (params: {
  bo: string[];
  studio: string[];
  category?: string;
  start_date: string;
  end_date: string;
  granularity: 'monthly' | 'quarter' | 'year';
}): Promise<any> => {
  return http.post('/api/v1/globalpedia_pc/studio_overall/calendar/list', params);
};
